/**
 * Webinar Countdown Banner Component
 *
 * Carousel banner displaying upcoming/live webinars with auto-advance.
 * Shows countdown timers and allows users to navigate between multiple webinars.
 */

import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Webinar } from '@immopersian/common';
import { WebinarCard } from './WebinarCard';

interface WebinarCountdownBannerProps {
  webinars: Webinar[];
  onWebinarClick: (webinar: Webinar) => void;
}

export function WebinarCountdownBanner({ webinars, onWebinarClick }: WebinarCountdownBannerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-advance disabled - users can manually navigate with arrows
  // useEffect(() => {
  //   if (webinars.length <= 1) return;
  //   const interval = setInterval(() => {
  //     setCurrentIndex((prev) => (prev + 1) % webinars.length);
  //   }, 8000);
  //   return () => clearInterval(interval);
  // }, [webinars.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + webinars.length) % webinars.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % webinars.length);
  };

  if (!webinars || webinars.length === 0) {
    return null;
  }

  return (
    <div className="bg-brand-dark/5 border-b-2 border-brand-terracotta/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="relative">
          {/* Carousel container */}
          <div className="overflow-hidden rounded-xl">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {webinars.map((webinar) => (
                <WebinarCard
                  key={webinar.id}
                  webinar={webinar}
                  onClick={() => onWebinarClick(webinar)}
                />
              ))}
            </div>
          </div>

          {/* Navigation arrows - only show if multiple webinars */}
          {webinars.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors z-10"
                aria-label="Previous webinar"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors z-10"
                aria-label="Next webinar"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Indicators */}
              <div className="flex justify-center gap-2 mt-4">
                {webinars.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex
                        ? 'bg-brand-terracotta w-6'
                        : 'bg-brand-dark/30 hover:bg-brand-dark/50'
                    }`}
                    aria-label={`Go to webinar ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
